import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Bar as BarModel } from '../../models/bar.model';
import { Picture } from '../../models/picture.model';
import { BarService } from '../../services/bar.service';
import { PictureService } from '../../services/picture.service';
import { PhoneFormatPipe } from '../../pipes/phone-format.pipe';
import { MapPreview } from '../../components/map-preview/map-preview';
import { Gallery } from '../../components/gallery/gallery';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-bar',
  imports: [CommonModule, PhoneFormatPipe, MapPreview, Gallery],
  templateUrl: './bar.html',
  styleUrl: './bar.scss',
})
export class Bar implements OnInit {
  bar: BarModel | null = null;
  isLoading = true;
  currentDaySchedule: { day: string; hours: string } | null = null;
  allSchedule: { day: string; hours: string }[] = [];

  pictures: Picture[] = [];
  picturesLoading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private barService: BarService,
    private pictureService: PictureService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    // Scroll a arriba
    setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
    
    const barId = this.route.snapshot.paramMap.get('id');
    console.log('Bar ID from route:', barId);
    if (barId) {
      await this.loadBar(barId);
      this.loadPictures(barId);
    } else {
      this.isLoading = false;
      console.error('No bar ID provided');
    }
  }

  async loadBar(barId: string) {
    try {
      this.isLoading = true;
      this.cdr.detectChanges();
      console.log('Loading bar with ID:', barId);
      
      const result = await this.barService.getBar(barId);
      console.log('Bar loaded:', result);
      
      if (!result) {
        console.error('No bar data received');
        this.bar = null;
      } else {
        this.bar = result;
        this.parseSchedule();
      }
    } catch (error) {
      console.error('Error loading bar:', error);
      this.bar = null;
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
      console.log('Loading finished. isLoading:', this.isLoading, 'bar:', this.bar);
    }
  }

  async loadPictures(barId: string) {
    try {
      this.picturesLoading = true;
      this.pictures = await this.pictureService.getBarPictures(barId);
    } catch (error) {
      console.error('Error loading pictures:', error);
      this.pictures = [];
    } finally {
      this.picturesLoading = false;
      this.cdr.detectChanges();
    }
  }

  parseSchedule() {
    if (!this.bar?.regularOpeningHours) return;

    const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const today = new Date().getDay();
    const todayName = days[today];

    this.allSchedule = this.bar.regularOpeningHours.map(schedule => {
      const parts = schedule.split(': ');
      return {
        day: parts[0],
        hours: parts[1] || 'Cerrado'
      };
    });

    this.currentDaySchedule = this.allSchedule.find(s => 
      s.day.toLowerCase() === todayName.toLowerCase()
    ) || null;
  }

  get isOpenNow(): boolean {
    if (!this.currentDaySchedule || !this.bar?.regularOpeningHours) return false;
    
    const schedule = this.bar.regularOpeningHours.find(s => 
      s.toLowerCase().startsWith(this.currentDaySchedule!.day.toLowerCase())
    );
    
    if (!schedule) return false;

    return this.checkIfOpen(schedule);
  }

  private checkIfOpen(schedule: string): boolean {
    const timeRanges = schedule.split(': ')[1];
    if (!timeRanges || timeRanges === 'Cerrado') return false;

    const ranges = timeRanges.split(', ');
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;

    for (const range of ranges) {
      const [start, end] = range.split('–').map(t => t.trim());
      const [startHour, startMin] = start.split(':').map(Number);
      const [endHour, endMin] = end.split(':').map(Number);
      
      const startTime = startHour * 60 + startMin;
      let endTime = endHour * 60 + endMin;
      
      if (endTime < startTime) {
        endTime += 24 * 60;
      }
      
      let checkTime = currentTime;
      if (currentTime < startTime && endTime > 24 * 60) {
        checkTime += 24 * 60;
      }
      
      if (checkTime >= startTime && checkTime <= endTime) {
        return true;
      }
    }

    return false;
  }

  openGoogleMaps() {
    if (this.bar?.googleMapsUri) {
      window.open(this.bar.googleMapsUri, '_blank');
    } else if (this.bar?.coordinates) {
      const url = `https://www.google.com/maps/search/?api=1&query=${this.bar.coordinates.latitude},${this.bar.coordinates.longitude}`;
      window.open(url, '_blank');
    }
  }

  callBar() {
    if (this.bar?.phone) {
      window.location.href = `tel:${this.bar.phone}`;
    }
  }

  visitWebsite() {
    if (this.bar?.web) {
      window.open(this.bar.web, '_blank');
    }
  }

  shareBar() {
    if (!this.bar) return;
    const url = `${environment.shareUrl.protocol}://${environment.shareUrl.domain}/${environment.shareUrl.path}?barId=${this.bar.id}`;
    if (navigator.share) {
      navigator.share({
        title: this.bar.name,
        text: `Mira este bar en BarBook: ${this.bar.name}`,
        url
      }).catch(() => {});
    } else {
      this.copyToClipboard(url);
    }
  }

  copied = false;

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.copied = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.copied = false;
        this.cdr.detectChanges();
      }, 2000);
    });
  }

  goBack() {
    this.router.navigate(['/search']);
  }
}
